/**
 * The Partner Portal sidebar, from one list. An entry with a `to` is a real, working page. An entry without one is NOT built and is shown as "Not available yet" and cannot be opened: the
 * portal never presents an unsupported section as a working feature. When a section is built, give it a `to`.
 */
export interface PartnerNavItem {
  label: string;
  to?: string;
  /** Why it is not available, shown to the person. */
  reason?: string;
}

export interface PartnerNavGroup {
  label: string;
  items: PartnerNavItem[];
}

const NEEDS_CLIENT_ACCESS = 'Needs access to a client company, which is not built yet.';
const NEEDS_REVENUE = 'Needs partner agreements and billing, which are not built yet.';

export const PARTNER_NAV: PartnerNavGroup[] = [
  {
    label: 'Dashboard',
    items: [
      { label: 'Portfolio Overview', to: '/partner/dashboard' },
      { label: 'Alerts and To-Dos', reason: 'Needs payroll and filing data from clients, which is not built yet.' },
    ],
  },
  {
    label: 'Clients',
    items: [
      { label: 'All Clients', to: '/partner/clients' },
      { label: 'Add Client', to: '/partner/clients/new' },
      { label: 'Client Onboarding', reason: 'The guided setup checklist is not built yet.' },
      { label: 'Authorization Requests', to: '/partner/clients?status=pending' },
    ],
  },
  {
    label: 'Payroll',
    items: [
      { label: 'Upcoming Payrolls', reason: NEEDS_CLIENT_ACCESS },
      { label: 'Payroll Calendar', reason: NEEDS_CLIENT_ACCESS },
      { label: 'Awaiting Preparation', reason: NEEDS_CLIENT_ACCESS },
      { label: 'Awaiting Review', reason: NEEDS_CLIENT_ACCESS },
      { label: 'Awaiting Client Approval', reason: NEEDS_CLIENT_ACCESS },
      { label: 'Completed Payrolls', reason: NEEDS_CLIENT_ACCESS },
      { label: 'Payroll Exceptions', reason: NEEDS_CLIENT_ACCESS },
    ],
  },
  {
    label: 'Reports',
    items: [
      { label: 'Client Payroll Reports', reason: NEEDS_CLIENT_ACCESS },
      { label: 'Portfolio Reports', reason: NEEDS_CLIENT_ACCESS },
      { label: 'Partner Activity Reports', reason: 'The partner activity report is not built yet.' },
    ],
  },
  {
    label: 'Revenue',
    items: [
      { label: 'Revenue Overview', reason: NEEDS_REVENUE },
      { label: 'Client Revenue', reason: NEEDS_REVENUE },
      { label: 'Statements', reason: NEEDS_REVENUE },
      { label: 'Payment History', reason: NEEDS_REVENUE },
      { label: 'Adjustments', reason: NEEDS_REVENUE },
    ],
  },
  {
    label: 'Team',
    items: [
      { label: 'Team Members', to: '/partner/team' },
      { label: 'Client Assignments', reason: "Assign people from each client's page." },
      { label: 'Roles and Permissions', to: '/partner/team#roles' },
    ],
  },
  {
    label: 'Integrations',
    items: [
      { label: 'Connected Applications', reason: 'Partner integrations are not built yet.' },
      { label: 'Accounting Connections', reason: 'Partner integrations are not built yet.' },
      { label: 'Partner API', reason: 'The Partner API is not built yet.' },
      { label: 'Import / Export', reason: NEEDS_CLIENT_ACCESS },
    ],
  },
  {
    label: 'Settings',
    items: [
      { label: 'Partner Profile', to: '/partner/settings/profile' },
      { label: 'Branding', reason: 'Partner branding is not built yet.' },
      { label: 'Notifications', reason: 'Partner notifications are not built yet.' },
      { label: 'Security', to: '/partner/settings/security' },
      { label: 'Agreements', to: '/partner/settings/agreements' },
      { label: 'Support', reason: 'Partner support cases are not built yet.' },
    ],
  },
];

/** What a client workspace will contain. None of it opens yet, because a partner cannot yet enter a client company. */
export const CLIENT_WORKSPACE_SECTIONS = [
  'Client Overview',
  'Employees',
  'Compensation',
  'Benefits and Deductions',
  'Run Payroll',
  'Payroll Review',
  'Payments',
  'Taxes and Filings',
  'Reports',
  'Import / Export',
  'Accounting',
  'Client Settings',
  'Authorization History',
] as const;
